// Atmosphere.tsx — Planet atmosphere as a limb-hugging glow. Per fragment we take
// the view ray's closest approach to the planet centre (impact parameter b);
// brightness peaks at the planet's surface radius and falls off with altitude, so
// it hugs the limb and fades outward — not a detached bubble on the shell edge.
//
// The spatial uniforms (centre, radii) are refreshed in onBeforeRender, not
// useFrame: that fires during the render traversal once matrixWorld is current, so
// the glow never lags the orbiting planet by a frame.
import { useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { AdditiveBlending, Color, Quaternion, Vector3, Vector4 } from 'three';
import type { Mesh } from 'three';
import type { PlanetKind } from '../types';
import { SHADOW_PARS, makeShadowUniforms, setOccluders } from './shadow';
import type { Occluder } from './shadow';

// Matches the surface shader's city cap; the night-limb city glow loops over this.
const MAX_CITY_GLOW = 50;

const vert = /* glsl */ `
  varying vec3 vWorldPos;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const frag = /* glsl */ `
  precision highp float;
  uniform vec3 uCameraPos;
  uniform vec3 uSunPos;
  uniform vec3 uCenter;
  uniform float uPlanetRadius;
  uniform float uAtmoRadius;
  uniform vec3 uColor;
  uniform vec3 uSunTint;
  uniform float uIntensity;
  uniform float uFalloff;
  uniform int uCityCount;
  uniform vec4 uCityW[${MAX_CITY_GLOW}]; // xyz: world dir, w: sprawl radius
  uniform vec3 uCityGlowCol;
  varying vec3 vWorldPos;
  ${SHADOW_PARS}
  void main() {
    vec3 d = normalize(vWorldPos - uCameraPos);
    float tca = max(dot(uCenter - uCameraPos, d), 0.0);
    vec3 closest = uCameraPos + d * tca;
    float b = length(uCenter - closest);
    float band = max(uAtmoRadius - uPlanetRadius, 1e-4);
    float density = exp(-abs(b - uPlanetRadius) / band * uFalloff);
    vec3 toLimb = normalize(closest - uCenter);
    float ndl = dot(toLimb, normalize(uSunPos - uCenter));
    // Respect the planet's shadow: the glow fades through the terminator to near
    // dark on the night/shadowed limb, with a warm twilight band at the edge.
    float lit = smoothstep(-0.18, 0.25, ndl);
    float dayNight = mix(0.04, 1.0, lit);
    float sun = max(ndl, 0.0);
    vec3 tint = mix(uColor, uSunTint, sun * sun);
    // A moon transiting between the star and this limb point eclipses the glow.
    float ecl = sunVisibility(closest);
    float a = clamp(density * dayNight * uIntensity * mix(0.12, 1.0, ecl), 0.0, 1.0);

    // City lights scattering into the NIGHT-side limb: where a settled coast sits at
    // this limb direction and is in darkness, its lights wash the atmosphere ring with
    // a warm glow — the airglow of cities seen from orbit. Concentrated at the limb
    // (× density) and on the night side only (where the atmosphere is otherwise dark).
    float citySide = smoothstep(0.15, -0.4, ndl);
    float cg = 0.0;
    if (uCityCount > 0 && citySide * density > 0.002) {
      float cityGlow = 0.0;
      for (int i = 0; i < ${MAX_CITY_GLOW}; i++) {
        if (i >= uCityCount) break;
        float c = dot(toLimb, uCityW[i].xyz);
        float spr = uCityW[i].w;
        cityGlow = max(cityGlow, exp(-(1.0 - c) / max(spr * spr * 0.5, 1e-4)));
      }
      cg = cityGlow * citySide * density * ecl;
    }

    vec3 col = tint * a + uCityGlowCol * cg * 1.6;
    float outA = clamp(a + cg * 0.85, 0.0, 1.0);
    gl_FragColor = vec4(col, outA);
  }
`;

interface KindStyle {
  color: string;
  sunTint: string;
  scale: number;
  intensity: number;
  falloff: number;
}

// A soft, pale limb glow — not a thick bubble, not a saturated neon ring. The
// colours are deliberately desaturated and the intensity low so additive blending
// can't blow them out to electric blue; a moderate falloff keeps it hugging the limb
// without concentrating into a hard band. Gas giants keep their deep, fat envelope.
// Airless classes (barren_dry/barren_cold/broken) are deliberately ABSENT — the
// component renders nothing when a kind has no style, so they show a bare limb.
// Tuned to read as a SOFT RIM hugging the limb (high falloff = tight band, modest
// intensity, modest scale) like the Stellaris planet art — not a fat bright halo
// that washes the whole disc pale. Toxic keeps a thicker, more visible shroud.
const ATMO: Partial<Record<PlanetKind, KindStyle>> = {
  tropical: { color: '#5fc8ff', sunTint: '#d6f4ff', scale: 1.32, intensity: 0.7, falloff: 4.4 },
  continental: { color: '#8fc0ff', sunTint: '#eaf4ff', scale: 1.3, intensity: 0.62, falloff: 4.6 },
  ocean: { color: '#5bb6ee', sunTint: '#dff2ff', scale: 1.32, intensity: 0.68, falloff: 4.4 },
  savanna: { color: '#d8b878', sunTint: '#f4e2b0', scale: 1.3, intensity: 0.58, falloff: 4.6 },
  arid: { color: '#caa074', sunTint: '#eed8b4', scale: 1.28, intensity: 0.5, falloff: 4.8 },
  desert: { color: '#d6b87f', sunTint: '#ffd9a0', scale: 1.26, intensity: 0.46, falloff: 4.9 },
  arctic: { color: '#c6dcea', sunTint: '#eef6fb', scale: 1.3, intensity: 0.58, falloff: 4.5 },
  tundra: { color: '#b6c2c8', sunTint: '#e4eaee', scale: 1.3, intensity: 0.52, falloff: 4.6 },
  alpine: { color: '#8fc4e6', sunTint: '#e6f2ff', scale: 1.32, intensity: 0.62, falloff: 4.5 },
  volcanic: { color: '#6b4a3a', sunTint: '#a8826a', scale: 1.28, intensity: 0.5, falloff: 4.7 },
  frozen: { color: '#cfe2ee', sunTint: '#f2f8fc', scale: 1.24, intensity: 0.3, falloff: 4.9 },
  // Gas keeps its proven fat, soft envelope (tuned so it reads as a halo, not glare).
  gas: { color: '#9aa9c4', sunTint: '#d7b98a', scale: 1.7, intensity: 1.0, falloff: 3.8 },
  molten: { color: '#8a4a30', sunTint: '#d88a50', scale: 1.32, intensity: 0.55, falloff: 4.4 },
  toxic: { color: '#aec43a', sunTint: '#e2f08a', scale: 1.42, intensity: 0.95, falloff: 3.7 },
};

interface Props {
  planetRadius: number;
  /** World position of the star this planet orbits (== system.position). */
  sunWorldPos: Vector3;
  /** Star core radius (penumbra size for the analytic eclipse). */
  sunRadius: number;
  /** The planet's moons — they eclipse the atmosphere as they transit. */
  occluders: Occluder[];
  kind: PlanetKind;
  segments?: number;
  /** Number of coastal cities whose lights scatter into the night-side limb (0 = none). */
  cityCount?: number;
  /** Per-city WORLD-space unit direction (xyz) + sprawl radius (w), length MAX_CITY_GLOW.
   *  Updated each frame by Planet.tsx (the surface spins, so world dirs change). */
  cityWorld?: Vector4[];
  /** Warm city-light tint for the limb airglow. */
  cityGlowColor?: string;
}

export function Atmosphere({
  planetRadius,
  sunWorldPos,
  sunRadius,
  occluders,
  kind,
  segments = 48,
  cityCount = 0,
  cityWorld,
  cityGlowColor = '#ffce85',
}: Props) {
  const style = ATMO[kind];
  const meshRef = useRef<Mesh>(null);
  const camera = useThree((state) => state.camera);
  const shadow = useMemo(makeShadowUniforms, []);
  const scratch = useMemo(
    () => ({ pos: new Vector3(), quat: new Quaternion(), scl: new Vector3() }),
    [],
  );

  const uniforms = useMemo(
    () => ({
      uCameraPos: { value: new Vector3() },
      uSunPos: { value: new Vector3() },
      uCenter: { value: new Vector3() },
      uPlanetRadius: { value: planetRadius },
      uAtmoRadius: { value: planetRadius * (style?.scale ?? 1.6) },
      uColor: { value: new Color(style?.color ?? '#5b8bd0') },
      uSunTint: { value: new Color(style?.sunTint ?? '#cf9b6a') },
      uIntensity: { value: style?.intensity ?? 1.5 },
      uFalloff: { value: style?.falloff ?? 3.5 },
      uCityCount: { value: 0 },
      uCityW: { value: Array.from({ length: MAX_CITY_GLOW }, () => new Vector4(1, 0, 0, 0)) },
      uCityGlowCol: { value: new Color(cityGlowColor) },
      ...shadow,
    }),
    [style, planetRadius, shadow, cityGlowColor],
  );

  // Runs during the render traversal — matrixWorld is current, so no frame lag.
  const onBeforeRender = () => {
    const m = meshRef.current;
    if (!m || !style) {
      return;
    }
    m.matrixWorld.decompose(scratch.pos, scratch.quat, scratch.scl);
    const atmoR = planetRadius * scratch.scl.x;
    uniforms.uCenter.value.copy(scratch.pos);
    uniforms.uAtmoRadius.value = atmoR;
    uniforms.uPlanetRadius.value = atmoR / style.scale;
    uniforms.uCameraPos.value.copy(camera.position);
    uniforms.uSunPos.value.copy(sunWorldPos);
    // Coastal-city limb glow: copy the (per-frame, world-space) city directions in.
    uniforms.uCityCount.value = cityWorld ? Math.min(cityCount, MAX_CITY_GLOW) : 0;
    if (cityWorld) {
      const dst = uniforms.uCityW.value as Vector4[];
      for (let i = 0; i < uniforms.uCityCount.value; i++) {
        dst[i].copy(cityWorld[i]);
      }
    }
    setOccluders(shadow, sunWorldPos, sunRadius, occluders);
  };

  if (!style) {
    return null;
  }

  return (
    <mesh ref={meshRef} scale={style.scale} renderOrder={3} onBeforeRender={onBeforeRender}>
      <sphereGeometry args={[planetRadius, segments, segments]} />
      <shaderMaterial
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={AdditiveBlending}
        toneMapped={false}
      />
    </mesh>
  );
}
